
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { applyLeave } from "../../redux/actions/applyLeaveActions";
import { fetchLeaveTypesMaster } from "../../redux/actions/leaveTypeActions";
import {
  searchEmployee,
  clearEmployees as clearSearchEmployeeResults,
} from "../../redux/actions/SearchEmployeeActions";
import { toast } from "react-hot-toast";

const ApplyLeave = () => {
  const dispatch = useDispatch();

  // Apply Leave state
  const { loading, success, error } = useSelector(
    (state) => state.applyLeave || {}
  );

  // Leave Types state
  const {
    masterLeaveTypes: leaveTypes = [],
    loading: leaveTypesLoading = false,
  } = useSelector((state) => state.leaveType || {});

  // Employee search state
  const {
    employees = [],
    loading: empLoading = false,
  } = useSelector((state) => state.employeeSearch || {});

  // Form states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("pending");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Employee search states
  const [searchTerm, setSearchTerm] = useState("");
  const [userId, setUserId] = useState("");

  // Fetch leave types on mount
  useEffect(() => {
    dispatch(fetchLeaveTypesMaster());
  }, [dispatch]);

  // Success notification and reset form on success
  useEffect(() => {
    if (success) {
      //toast.success("Leave applied successfully");
      resetForm();
      setUserId("");
    }
  }, [success]);

  // Error notification
  useEffect(() => {
    if (error) {
      toast.error(typeof error === "string" ? error : "Something went wrong");
    }
  }, [error]);

  // Debounced employee search effect
  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (!userId && trimmed.length > 2) {
      const handler = setTimeout(() => {
        dispatch(searchEmployee(trimmed));
      }, 500);
      return () => clearTimeout(handler);
    } else if (!userId) {
      dispatch(clearSearchEmployeeResults());
    }
  }, [searchTerm, userId, dispatch]);

  // Clean up photo preview URL
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setLeaveTypeId("");
    setReason("");
    setStatus("pending");
    setPhoto(null);
    setPhotoPreview(null);
    setSearchTerm("");
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleSelectEmployee = (emp) => {
    setUserId(emp.id);
    setSearchTerm(`${emp.name} (${emp.phone || "mobile"})`);
    dispatch(clearSearchEmployeeResults());
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!startDate || !endDate || !leaveTypeId || !reason || !userId) {
      toast.error("Please fill all required fields");
      return;
    }

    dispatch(
      applyLeave({
        start_date: startDate,
        end_date: endDate,
        leave_gov_type: leaveTypeId, // match API key
        reason,
        photo,
        // status,
         status: "approved",
        user_id: userId,
      })
    );
  };

  const showEmployeeDropdown = !empLoading && employees.length > 0 && !userId;

  return (
    <div className="w-full min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-6 relative">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Apply Leave</h1>

        {/* EMPLOYEE SEARCH */}
        <div className="mb-8 relative">
          <label className="block text-gray-700 font-medium mb-1">
            Employee <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (userId) setUserId("");
            }}
            placeholder="Search by name or Mobile (min 3 chars)"
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
          />
          {empLoading && (
            <div className="text-sm text-gray-500 mt-1">Searching...</div>
          )}
          {showEmployeeDropdown && (
            <ul className="border border-gray-300 rounded-md mt-1 bg-white max-h-48 overflow-y-auto shadow-lg z-10 absolute w-full">
              {employees.map((emp) => (
                <li
                  key={emp.id}
                  onClick={() => handleSelectEmployee(emp)}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-cyan-100 cursor-pointer"
                >
                  {emp.photo ? (
                    <img
                      src={emp.photo}
                      alt={emp.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm">
                      N/A
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-800">
                      {emp.name} {emp.phone ? `(${emp.phone})` : ""}
                    </div>
                    <div className="text-xs text-gray-500">
                      {emp.designation} | {emp.district} | {emp.municipality}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* APPLY LEAVE FORM */}
        {userId ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="bg-green-100 p-3 rounded-md border border-green-300">
              <p className="text-sm text-green-800">
                Applying leave for:{" "}
                <strong className="font-semibold">{searchTerm}</strong>
              </p>
            </div>

            {/* User ID readonly */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                User ID
              </label>
              <input
                type="text"
                value={userId}
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 shadow-sm"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (endDate && e.target.value > endDate) {
                    setEndDate("");
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                required
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                required
              />
            </div>

            {/* Leave Type */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Leave Type <span className="text-red-500">*</span>
              </label>
              <select
                value={leaveTypeId}
                onChange={(e) => setLeaveTypeId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                required
              >
                <option value="">Select Leave Type</option>
                {leaveTypesLoading && <option disabled>Loading...</option>}
                {!leaveTypesLoading && leaveTypes.length > 0 ? (
                  leaveTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))
                ) : (
                  !leaveTypesLoading && (
                    <option disabled>No Leave Types Found</option>
                  )
                )}
              </select>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                required
              />
            </div>

            {/* Status */}
            {/* <div>
              <label className="block text-gray-700 font-medium mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                disabled
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div> */}

            {/* Photo Upload */}
            <div>
              <label className="block text-gray-700 font-medium mb-1">
                Upload Photo
              </label>
              <input
                type="file"
                onChange={handlePhotoChange}
                accept="image/*"
                capture="environment"
                className="w-full"
              />
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="mt-2 w-32 h-32 object-cover rounded"
                />
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-4 py-2 text-white font-medium rounded-md transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-cyan-600 hover:bg-cyan-700"
              }`}
            >
              {loading ? "Applying..." : "Apply Leave"}
            </button>
          </form>
        ) : (
          <p className="text-center text-gray-600">
            Please select an employee above to apply leave.
          </p>
        )}
      </div>
    </div>
  );
};

export default ApplyLeave;
